import Card from '@mui/material/Card';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch, useAppSelector } from '../store/store';
import { Button, Grid, Typography } from '@mui/material';
import { handleRemovePredictionResults, previewPredictionParametersHandler } from '../store/prediction-slice/prediction-slice';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { AppRoutes } from '../axios/endPoints';
import { useNavigate } from 'react-router';
import { Header } from './common/Header';
import { getUrinarySystemBackground, getUrinarySystemBackgroundNo, getUrinarySystemIllustration } from './PredictionResultsHelper';
import { TypographyPredictionToExpel, TypographyProbabilityToExpel, TypographyMaximalStone } from './PredictionResultsHelper';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
      padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
      padding: theme.spacing(1),
    },
}));
  
const PreviewPredictionParametersDialog = () => 
{
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { predictionParameters, previewPredictionParameters }= useAppSelector((state : RootState) => state.predictions);

    function handleClose() {
        dispatch(previewPredictionParametersHandler(false));
    }

    return(
        <BootstrapDialog
            onClose={handleClose}
            aria-labelledby="customized-dialog-title"
            open={previewPredictionParameters}
            sx={{
                "& .MuiDialog-container": {
                  "& .MuiPaper-root": {
                    width: "100%",
                    maxWidth: "500px"
                  },
                },
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">{t('translations:PREDICTION_RESULTS_REVIEW_TITLE')}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers>
                { predictionParameters ? 
                <Grid>
                    <Grid item><Typography variant="subtitle1">{t('translations:PREDICTIONS_SEX_LABEL')}: {predictionParameters.sex}</Typography></Grid>
                    <Grid item><Typography variant="subtitle1">{t('translations:PREDICTIONS_AGE_LABEL')}: {predictionParameters.age}</Typography></Grid>
                    <Grid item><Typography variant="subtitle1">{t('translations:PREDICTIONS_WEIGHT_POUNDS_LABEL')}: {predictionParameters.weightPounds}</Typography></Grid>
                    <Grid item><Typography variant="subtitle1">{t('translations:PREDICTIONS_WEIGHT_KG_LABEL')}: {predictionParameters.weight}</Typography></Grid>
                    <Grid item><Typography variant="subtitle1">{t('translations:PREDICTIONS_HEIGHT_LABEL')}: {predictionParameters.height}</Typography></Grid>
                    <Grid item><Typography variant="subtitle1">{t('translations:PREDICTIONS_STONE_SIZE_LABEL')}: {predictionParameters.stoneSizeInMilimeters}</Typography></Grid>
                    <Grid item><Typography variant="subtitle1">{t('translations:PREDICTIONS_STONE_POSITION_LABEL')}: {predictionParameters.stonePosition}</Typography></Grid>
                    <Grid item><Typography variant="subtitle1">{t('translations:PREDICTIONS_FEVER_LABEL')}: {predictionParameters.fever}</Typography></Grid>
                    <Grid item><Typography variant="subtitle1">{t('translations:PREDICTIONS_SYMPTOMS_LABEL')}: {predictionParameters.symptomsDurationInHours}</Typography></Grid>
                    <Grid item><Typography variant="subtitle1">{t('translations:PREDICTIONS_PREVIOUS_STONE_EXPLUSION_LABEL')}: {predictionParameters.previousStoneExplusion}</Typography></Grid>
                    <Grid item><Typography variant="subtitle1">{t('translations:PREDICTIONS_PREVIOUS_TREATMENT_LABEL')}: {predictionParameters.previousUrologicTreatment}</Typography></Grid>
                </Grid> : ''}
            </DialogContent>
            <DialogActions>
                <Button variant='contained' className='button-color' autoFocus onClick={handleClose}>
                    {t('translations:PREDICTION_RESULTS_CLOSE_REVIEW_BUTTON')}
                </Button>
            </DialogActions>
        </BootstrapDialog>
    )
}

const PredictionResults = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { predictionParameters, predictionResults } = useAppSelector((state: RootState) => state.predictions);

    var urinarySystemBackground = getUrinarySystemBackground();
    var urinarySystemBackgroundNo = getUrinarySystemBackgroundNo(predictionResults.ableToExpel, predictionResults.imageParams.stonePosition, predictionResults.imageParams.stoneSizeInMilimeters);
    var urinarySystemIllustration = getUrinarySystemIllustration(predictionResults.ableToExpel, predictionResults.imageParams.stonePosition, predictionResults.imageParams.stoneSizeInMilimeters);

    function handlePreviewPredictionParamters(preview: boolean) {
        dispatch(previewPredictionParametersHandler(preview))
    }

    return(
        <Card style={{marginTop: '10px'}}>
            <Header title={t('translations:PREDICTION_RESULTS_TITLE')} />
            {
                predictionResults === null ? 
                <Grid display="flex" p={2} justifyContent="center" alignItems="center">
                    <Typography variant="h5">{t('translations:PREDICTION_RESULTS_NO_RESULTS_AVAILABLE')}</Typography>
                </Grid> : 
                <>
                <Grid container sx={{ flexDirection: { xs: "column", md: "row" } }} p={2}>
                    <Grid item md={6} xs={12} p={2} display="flex" justifyContent="center" alignItems="center">
                        <Card variant="outlined" style={{ width: '100%', height: '100%' }}>
                            <Grid container style={{ height: '100%' }} justifyContent="center" alignItems="center" textAlign="center">
                                <Grid container justifyContent="center" alignItems="center" textAlign="center">
                                    <Grid mb={1} item>
                                        <Grid container display="flex" flexDirection="column">
                                            <Grid item>
                                                {
                                                    predictionParameters && (predictionParameters.firstName || predictionParameters.middleName || predictionParameters.lastName) ?
                                                    <Grid item><Typography variant='h6'>{t('translations:PREDICTION_RESULT_PATIENT_NAME', { name: `${predictionParameters.firstName} ${predictionParameters.middleName} ${predictionParameters.lastName}` })}</Typography></Grid> : ''
                                                }
                                            </Grid>
                                            <Grid item>
                                                {
                                                    predictionParameters && predictionParameters.patientDateOfBirth ?
                                                    <Grid item><Typography variant='h6'>{t('translations:PREDICTION_RESULT_PATIENT_DATE_OF_BIRTH', { dateOfBirth: predictionParameters.patientDateOfBirth })}</Typography></Grid> : ''
                                                }
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    {
                                    predictionResults.ableToExpel === 0 ||  predictionResults.ableToExpel === 1 ?
                                    <Grid item p={2} textAlign="center">
                                        <TypographyPredictionToExpel ableToExpel={predictionResults.ableToExpel} stoneSizeInMilimeters={predictionParameters.stoneSizeInMilimeters} />
                                        <TypographyProbabilityToExpel ableToExpel={predictionResults.ableToExpel} probabilityToExpel={predictionResults.probabilityToExpel} />
                                        <br />
                                        <TypographyMaximalStone maximalStoneSizeToExpel={predictionResults.maximalStoneSizeToExpel} maximalStoneSizeProbabilityToExpel={predictionResults.maximalStoneSizeProbabilityToExpel} />
                                    </Grid> :
                                    <></>
                                    }
                                    <Grid item p={2} textAlign="center">
                                        <Button className='button-color' variant="contained" onClick={() => {
                                            dispatch(handleRemovePredictionResults());
                                            navigate(AppRoutes.PREDICTIONS);
                                        }}>{t('translations:PREDICTION_RESULTS_CLOSE_BUTTON')}</Button>
                                    </Grid>
                                    <Grid item p={2} textAlign="center">
                                        <Button className='button-color' variant="contained" onClick={() => 
                                        { 
                                            handlePreviewPredictionParamters(true);
                                        }}>{t('translations:PREDICTION_RESULTS_REVIEW_PATIENT_PARAMS')}</Button>
                                    </Grid>
                                    <Grid item p={2} textAlign="center">
                                        <Button className='button-color' variant="contained" onClick={() => 
                                        { 
                                            window.print();
                                        }}>{t('translations:EXPORT_RESULTS_BUTTON')}</Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>
                    <Grid item md={6} xs={12} p={2} display="flex" justifyContent="center" alignItems="center">
                        <Card variant="outlined" style={{ width: '100%', height: '100%' }}>  
                            <Grid container style={{ height: '100%' }} justifyContent="center" alignItems="center" textAlign="center">
                                <div style={{ display:'inline-block', position: 'relative', top: 0, left: 0 }}>
                                    <img src={urinarySystemBackground} alt="bckg" style={{ maxWidth: '100%', height: '600px', position: 'relative', top: 0, left: 0, zIndex: 5 }} />
                                    {
                                    urinarySystemBackgroundNo === "" ?
                                    <></>
                                    :
                                    <img src={urinarySystemBackgroundNo} alt="bckgno" style={{ maxWidth: '100%', height: '600px', position: 'absolute', top: 0, left: 0, zIndex: 6 }} />
                                    }
                                    <img src={urinarySystemIllustration} alt="gif" style={{ maxWidth: '100%', height: '600px', position: 'absolute', top: 0, left: 0, zIndex: 7 }}/>
                                </div>
                            </Grid>
                        </Card>
                    </Grid>
                </Grid>
                </>
            }
            <PreviewPredictionParametersDialog />
        </Card>
    )
}

export default PredictionResults;